import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import Layout from '../components/Layout'
import PublicShell from '../components/PublicShell'
import Tooltip from '../components/Tooltip'
import FeeInput from '../components/fees/FeeInput'
import { CHART_THEMES } from '../components/dashboard/chartTheme'
import { useThemePreference } from '../theme/themePreference'
import { CLOSED_PAGE_SIZE, ClosedPager, formatR, pnlClass } from '../components/trackRecord/recordUI'
import EnginePausesPanel from '../components/trackRecord/EnginePausesPanel'
import EngineVersionsPanel from '../components/trackRecord/EngineVersionsPanel'
import { FEE_REFETCH_DEBOUNCE_MS, formatFeePct, isValidFee } from '../config/fees'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../config/glossary'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import useStartingFee from '../hooks/useStartingFee'
import { authAPI, feeCalculatorAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { FeeCalculatorRecord } from '../types'
import { formatPnlPct } from '../utils/pnl'
import { parseApiDate } from '../utils/signalExpiry'
import { displaySymbol } from '../utils/displaySymbol'

export { FEE_REFETCH_DEBOUNCE_MS }

const OUTCOME_WORD = { win: 'Win', loss: 'Loss', breakeven: 'Break-even' } as const

const Tile = ({
  label,
  hint,
  value,
  valueClass = 'text-gray-50',
  testId,
  children,
}: {
  label: string
  hint?: string
  value: string
  valueClass?: string
  testId: string
  children?: ReactNode
}) => (
  <div className="rounded-lg border border-terminal-border bg-terminal-surface p-4">
    <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
      {hint ? <Tooltip content={hint}>{label}</Tooltip> : label}
    </p>
    <p className={`mt-1 font-mono text-2xl font-bold tabular-nums ${valueClass}`} data-testid={testId}>
      {value}
    </p>
    {children && <div className="mt-1 text-xs text-gray-400">{children}</div>}
  </div>
)

/**
 * The personal fee calculator: public, no login (decided 2026-10-09).
 *
 * Every result elsewhere is gross, before fees; this is the one page that
 * applies a fee, and only the one the reader enters. The backend nets the
 * signal engine's record at it (GET /api/public/fee-calculator, the
 * track-record netting's chosen-fee path, fee only, no slippage).
 *
 * It opens at the reader's saved fee if they have one, else 0% (gross).
 * Signed in, Save stores the fee on the account; moving the slider afterwards
 * is a what-if and changes nothing until Save is pressed again. Signed out it
 * works the same, but nothing is saved.
 *
 * Signed in it renders inside the app's Layout (with the sidebar, where it is
 * linked next to Track Record); signed out, in the public record frame.
 */
const FeeCalculator = () => {
  const signedIn = useAuthStore((state) => Boolean(state.token))
  const Frame = signedIn ? Layout : PublicShell
  // This component sits outside its Frame, so it reads the theme the Frame
  // will show: the user's choice in Layout, always dark in PublicShell.
  const { resolved } = useThemePreference()
  const palette = CHART_THEMES[signedIn ? resolved : 'dark']
  const updateUser = useAuthStore((state) => state.updateUser)
  const { saved, setSaved, fee, setFee } = useStartingFee(signedIn)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')

  const settled = useDebouncedValue(fee, FEE_REFETCH_DEBOUNCE_MS)
  const [record, setRecord] = useState<FeeCalculatorRecord | null>(null)
  const [failed, setFailed] = useState(false)
  const [page, setPage] = useState(0)
  const latest = useRef(0)
  useEffect(() => {
    if (settled == null) return
    const id = ++latest.current
    feeCalculatorAPI
      .get(settled)
      .then((data) => {
        if (id !== latest.current) return
        setRecord(data)
        setFailed(false)
      })
      .catch((error) => {
        console.error('Fee calculator failed to load:', error)
        if (id === latest.current) setFailed(true)
      })
  }, [settled])

  const changeFee = (next: number) => {
    setFee(next)
    if (saveState !== 'saving') setSaveState('idle')
  }

  const save = async () => {
    if (fee == null) return
    setSaveState('saving')
    try {
      const data = await authAPI.saveMyFee(fee)
      setSaved(data.fee_pct_per_side)
      updateUser({ fee_pct_per_side: data.fee_pct_per_side })
      setSaveState('saved')
    } catch (error) {
      console.error('Saving the fee failed:', error)
      setSaveState('failed')
    }
  }

  // The figures on screen trail the input while a new fee is in flight.
  const pending = record == null || fee == null || record.fee_pct_per_side !== fee
  const atSaved = saved != null && fee != null && Math.abs(saved - fee) < 1e-9
  const chart = record
    ? [
        { n: 0, gross: 0, net: 0 },
        ...record.signals.map((signal, index) => ({
          n: index + 1,
          gross: signal.cumulative_gross_r,
          net: signal.cumulative_net_r,
        })),
      ]
    : []
  // Newest first, a page at a time, as on the Track Record. The totals and the
  // curve above still cover every closed signal.
  const rows = record ? record.signals.map((signal, index) => ({ n: index + 1, signal })).reverse() : []
  const pageCount = Math.max(1, Math.ceil(rows.length / CLOSED_PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const pageRows = rows.slice(currentPage * CLOSED_PAGE_SIZE, (currentPage + 1) * CLOSED_PAGE_SIZE)

  return (
    <Frame>
      <div className="space-y-5" data-testid="fee-calculator">
        <div>
          <h1 className="text-3xl font-bold text-gray-50">Fee calculator</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-400">
            Every result on FinCreator is shown before trading fees, because fees depend on your exchange and tier. Enter
            yours to see what it does to the signal engine&rsquo;s record.
          </p>
        </div>

        {fee == null ? (
          <p className="text-sm text-gray-500" data-testid="fee-loading">
            Loading your saved fee…
          </p>
        ) : (
          <>
            <FeeInput value={fee} onChange={changeFee} />

            <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="fee-save">
              {signedIn ? (
                <>
                  <button
                    type="button"
                    onClick={save}
                    disabled={saveState === 'saving' || atSaved || !isValidFee(fee)}
                    className="rounded-md bg-sky-600 px-3 py-1.5 font-semibold text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="fee-save-button"
                  >
                    {saveState === 'saving' ? 'Saving…' : 'Save as my fee'}
                  </button>
                  <span className="text-gray-400" data-testid="fee-saved">
                    {saved == null ? 'No fee saved yet.' : `Saved fee: ${formatFeePct(saved)} per side.`}
                    {saved != null && !atSaved && ' Moving the fee is a what-if until you save.'}
                  </span>
                  {saveState === 'saved' && (
                    <span className="text-green-300" role="status">
                      Saved.
                    </span>
                  )}
                  {saveState === 'failed' && (
                    <span className="text-red-300" role="alert">
                      Could not save. Try again.
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-400" data-testid="fee-signed-out">
                  Nothing is saved while you are signed out.{' '}
                  <Link to="/login" className="text-sky-300 underline hover:text-sky-200">
                    Sign in
                  </Link>{' '}
                  to save your fee.
                </span>
              )}
            </div>
          </>
        )}

        {failed && record == null && (
          <p className="text-sm text-red-300" role="alert">
            The record could not be loaded right now.
          </p>
        )}

        {record && (
          <div className={`space-y-5 transition-opacity ${pending ? 'opacity-60' : ''}`} aria-busy={pending}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Tile
                label="Total R after your fee"
                hint={GLOSSARY.totalAfterYourFee}
                value={formatR(record.total_net_r)}
                valueClass={pnlClass(record.total_net_r)}
                testId="calc-total-net-r"
              >
                <span data-testid="calc-total-gross-r">{formatR(record.total_gross_r)} before fees</span>
                {record.unmeasured_r > 0 && ` · ${record.unmeasured_r} without a usable stop, not in either total`}
              </Tile>
              <Tile
                label="Wins after fees"
                hint={GLOSSARY.winsAfterFees}
                value={`${record.wins_after_fees} of ${record.closed}`}
                testId="calc-wins-after-fees"
              >
                <span data-testid="calc-gross-record">
                  Record: {record.wins} win{record.wins === 1 ? '' : 's'}, {record.losses} loss
                  {record.losses === 1 ? '' : 'es'} (before fees, unchanged)
                </span>
              </Tile>
              <Tile label="Round trip" value={formatFeePct(record.round_trip_pct_of_entry)} testId="calc-round-trip">
                of the trade: {formatFeePct(record.fee_pct_per_side)} in, {formatFeePct(record.fee_pct_per_side)} out
              </Tile>
            </div>

            <section className="rounded-lg border border-terminal-border bg-terminal-surface p-4">
              <h2 className="text-sm font-semibold text-gray-100">Gross vs your fee</h2>
              <p className="text-xs text-gray-500">
                Each closed signal&rsquo;s R added up in close order, before fees and after{' '}
                {formatFeePct(record.fee_pct_per_side)} per side. Fee only, slippage not included.
              </p>
              <div
                className="mt-3 h-64"
                role="img"
                aria-label={`R over ${record.closed} closed signals: ${formatR(record.total_gross_r)} before fees, ${formatR(
                  record.total_net_r
                )} after ${formatFeePct(record.fee_pct_per_side)} per side`}
                data-testid="calc-curve"
              >
                {record.closed === 0 ? (
                  <p className="flex h-full items-center justify-center text-sm text-gray-500">No closed signals yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chart} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="n"
                        type="number"
                        domain={[0, 'dataMax']}
                        allowDecimals={false}
                        tickFormatter={(n: number) => (n === 0 ? 'start' : `#${n}`)}
                        tick={palette.axisTick}
                        tickLine={false}
                        axisLine={{ stroke: palette.grid }}
                        minTickGap={24}
                      />
                      <YAxis
                        orientation="right"
                        tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${Number(v.toFixed(2))}R`}
                        tick={palette.axisTick}
                        tickLine={false}
                        axisLine={false}
                        width={56}
                      />
                      <ReferenceLine y={0} stroke={palette.cursor} strokeDasharray="4 4" />
                      <ChartTooltip
                        formatter={(value: number, name: string) => [formatR(value), name]}
                        labelFormatter={(n: number) => (n === 0 ? 'Start' : `#${n}`)}
                        contentStyle={{ ...palette.tooltip, fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line
                        type="linear"
                        dataKey="gross"
                        name="Before fees"
                        stroke="#94a3b8"
                        strokeDasharray="4 3"
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        type="linear"
                        dataKey="net"
                        name={`After ${formatFeePct(record.fee_pct_per_side)} per side`}
                        stroke="#38bdf8"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-terminal-border bg-terminal-surface">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="calc-table">
                  <thead>
                    <tr className="border-b border-terminal-border text-left text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Closed</th>
                      <th className="px-3 py-2">Signal</th>
                      <th className="px-3 py-2">Outcome</th>
                      <th className="px-3 py-2 text-right">R before fees</th>
                      <th className="px-3 py-2 text-right">
                        <Tooltip content={GLOSSARY.rAfterYourFee}>R after your fee</Tooltip>
                      </th>
                      <th className="px-3 py-2 text-right">% before fees</th>
                      <th className="px-3 py-2 text-right">% after your fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(({ n, signal }) => (
                      <tr key={signal.signal_id} className="border-b border-terminal-border/60" data-testid="calc-row">
                        <td className="px-3 py-2 font-mono text-gray-500">{n}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-gray-400">
                          {signal.closed_at ? format(parseApiDate(signal.closed_at), 'MMM d, yyyy') : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-semibold text-gray-100">{displaySymbol(signal.symbol)}</span>{' '}
                          <span className="text-xs uppercase text-gray-400">{signal.signal_type}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-300">{OUTCOME_WORD[signal.outcome]}</td>
                        <td className={`px-3 py-2 text-right font-mono tabular-nums ${pnlClass(signal.gross_r ?? 0)}`}>
                          {signal.gross_r != null ? formatR(signal.gross_r) : '—'}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono tabular-nums ${pnlClass(signal.net_r ?? 0)}`}
                          data-testid="calc-row-net-r"
                        >
                          {signal.net_r != null ? formatR(signal.net_r) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono tabular-nums ${pnlClass(signal.gross_pnl_pct ?? 0)}`}>
                          {signal.gross_pnl_pct != null ? formatPnlPct(signal.gross_pnl_pct) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono tabular-nums ${pnlClass(signal.net_pnl_pct ?? 0)}`}>
                          {signal.net_pnl_pct != null ? formatPnlPct(signal.net_pnl_pct) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > CLOSED_PAGE_SIZE && (
                <div className="px-3 pb-3">
                  <ClosedPager page={currentPage} pageCount={pageCount} shown={pageRows.length} total={rows.length} onPage={setPage} />
                </div>
              )}
            </section>

            <EnginePausesPanel />
            <EngineVersionsPanel />

            <p className="text-xs text-gray-500" data-testid="calc-basis">
              {record.basis} The signal engine&rsquo;s own record: every real, scan-created signal that has closed,
              excluded signals left out, as on the{' '}
              {/* Signed out, /track-record needs a login and the record link is this page. */}
              {signedIn ? (
                <Link to="/track-record" className="text-sky-300 underline hover:text-sky-200">
                  Track Record
                </Link>
              ) : (
                'Track Record'
              )}
              .
            </p>
          </div>
        )}
      </div>
    </Frame>
  )
}

export default FeeCalculator
